import {useEffect, useState} from 'react'
import {
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
  StyledReportIcon,
  StyledDisclaimerText,
  StyledDialogActions,
  StyledDialogButton,
  StyledCircularProgress
} from '../FilterPanelStyle'
import SaveFilterSnackBar from './SaveFilterSnackBar'
import useDeleteSavedFilter from '../../../api/queries/SavedFilters/queryDeleteSavedFilter'
import {useSavedFilter} from '../../../store/hooks/savedFilter'
import PropTypes from 'prop-types'

const DeleteFiltersPopup = ({open, onClose, currentFilterName, onSuccess}) => {
  const [filterName, setFilterName] = useState(currentFilterName || '')
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState({show: false, message: ''})
  const {deleteSavedFilter, loading} = useDeleteSavedFilter()
  const {deleteSavedFilter: deleteFromStore} = useSavedFilter()

  const handleSnackbarToggle = () => {
    setIsSnackBarOpen(!isSnackBarOpen)
  }

  useEffect(() => {
    if (open) {
      setFilterName(currentFilterName || '')
      setErrorMessage({show: false, message: ''})
    }
  }, [open, currentFilterName])

  const handleDelete = async () => {
    if (currentFilterName !== '') {
      try {
        const result = await deleteSavedFilter({
          variables: {saved_filter_name: currentFilterName}
        })

        if (result?.data?.deleteSavedFilter?.success) {
          // Remove from Redux store
          deleteFromStore(currentFilterName)

          // Show success message
          handleSnackbarToggle()

          // Refetch the saved filters list
          onSuccess()

          // Close dialog
          handleClose()
        } else {
          setErrorMessage({
            show: true,
            message: result?.data?.deleteSavedFilter?.message || 'Failed to delete filter'
          })
        }
      } catch (err) {
        setErrorMessage({
          show: true,
          message: err?.message || 'Failed to delete filter. Please try again.'
        })
      }
    }
  }

  const handleClose = () => {
    if (loading) {
      return
    }

    setErrorMessage({show: false, message: ''})
    onClose('', 'delete')
  }

  // KEYBOARD NAVIGATION HANDLERS
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (loading) {
        return // Don't delete if already loading
      }
      handleDelete()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      handleClose()
    }
  }

  return (
    <>
      <StyledDialog
        open={open}
        keepMounted
        onClose={handleClose}
        onKeyDown={handleKeyDown}
        aria-labelledby='delete-filter-dialog-title'
      >
        <StyledDialogTitle>
          <StyledReportIcon />
          Are you sure you want to delete this saved filter?
        </StyledDialogTitle>
        {loading && <StyledCircularProgress size={20} />}
        <StyledDialogContent>
          <StyledDisclaimerText>Removing this filter will prevent you from accessing it again.</StyledDisclaimerText>
        </StyledDialogContent>
        <StyledDialogContent>
          <StyledDisclaimerText>
            Filter group name : <strong>{currentFilterName}</strong>
          </StyledDisclaimerText>
        </StyledDialogContent>
        {errorMessage.show && (
          <StyledDialogContent>
            <StyledDisclaimerText style={{color: 'red'}}>{errorMessage.message}</StyledDisclaimerText>
          </StyledDialogContent>
        )}

        {!loading && (
          <StyledDialogActions>
            <StyledDialogButton variant='outlined' onClick={handleClose} type='cancel' disabled={loading}>
              Cancel
            </StyledDialogButton>
            <StyledDialogButton
              variant='contained'
              color='error'
              onClick={handleDelete}
              type='delete'
              disabled={loading}
            >
              Delete
            </StyledDialogButton>
          </StyledDialogActions>
        )}
      </StyledDialog>

      <SaveFilterSnackBar
        isSnackBarOpen={isSnackBarOpen}
        handleSnackbarClose={handleSnackbarToggle}
        data={
          <>
            <strong>{filterName}</strong> deleted from saved filters
          </>
        }
        action='delete'
      />
    </>
  )
}

DeleteFiltersPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentFilterName: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired
}

export default DeleteFiltersPopup
